import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import type { PlayerBet } from '../ws/types'
import { SPRING_SOFT } from '../../../shared/motion.ts'

type Props = {
  players: PlayerBet[]
  selfId: number | null
}

export function PlayerList({ players, selfId }: Props) {
  const { t } = useTranslation()
  // Flash a row once when its status transitions to a terminal/review state.
  const prevStatus = useRef(new Map<number, PlayerBet['status']>())
  const flash = useRef(new Set<number>())
  useEffect(() => {
    const next = new Map<number, PlayerBet['status']>()
    for (const p of players) {
      const prev = prevStatus.current.get(p.userId)
      if (prev === 'active' && p.status !== 'active') {
        flash.current.add(p.userId)
      } else if (p.status === 'active') {
        flash.current.delete(p.userId)
      }
      next.set(p.userId, p.status)
    }
    prevStatus.current = next
  }, [players])

  if (!players.length) {
    return <div className="players empty">{t('noPlayers')}</div>
  }
  return (
    <ul className="players">
      {players.map((p) => (
        <motion.li
          key={p.userId}
          layout
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={SPRING_SOFT}
          className={`${statusClass(p.status)}${flash.current.has(p.userId) ? ' flash' : ''}`}
        >
          <span className="who">
            {p.userId === selfId ? t('you') : playerLabel(p)}
          </span>
          <span className="amt">{p.amount}</span>
          <span className="st">
            {p.status === 'cashed'
              ? t('cashoutAt', {
                  mult: p.cashoutAt?.toFixed(2),
                  payout: p.payout ?? 0,
                })
              : p.status === 'lost'
                ? t('notCashed')
                : p.status === 'pending'
                  ? t('payoutPending')
                  : t('inPlay')}
          </span>
        </motion.li>
      ))}
    </ul>
  )
}

function statusClass(s: PlayerBet['status']): string {
  if (s === 'cashed') return 'ok'
  if (s === 'lost') return 'bad'
  if (s === 'pending') return 'review'
  return 'live'
}

const BOT_ALIASES = [
  'Nova',
  'Kai',
  'Ren',
  'Mika',
  'Ash',
  'Leo',
  'Ivy',
  'Nico',
  'Sky',
  'Quinn',
  'Remy',
  'Jade',
]

function playerLabel(p: PlayerBet): string {
  if (p.isBot) {
    const n = Math.abs(p.userId)
    return BOT_ALIASES[(n - 1) % BOT_ALIASES.length] ?? 'Nova'
  }
  return `#${p.userId}`
}
